import React, { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2Icon, MailIcon, SendIcon } from 'lucide-react';
import { DiscoveryContactDetails } from './DiscoveryContactDetails';
import { DiscoveryPeopleContext } from './DiscoveryPeopleContext';
import { DiscoveryProjectDetails } from './DiscoveryProjectDetails';
import { DiscoverySystemContext } from './DiscoverySystemContext';
import { createDiscoveryMailto, initialValues, validateDiscovery } from '../data/discoveryForm';
import type { FormErrors, FormValues } from '../data/discoveryForm';
export function DiscoveryForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [mailtoUrl, setMailtoUrl] = useState('');
  function updateField<K extends keyof FormValues>(field: K, value: FormValues[K]) {
    setValues(current => ({
      ...current,
      [field]: value
    }));
    if (errors[field]) setErrors(current => ({
      ...current,
      [field]: undefined
    }));
  }
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateDiscovery(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      document.getElementById(Object.keys(nextErrors)[0])?.focus();
      return;
    }
    setMailtoUrl(createDiscoveryMailto(values));
  }
  if (mailtoUrl) {
    return <div className="border border-[#9bc6b5] bg-[#eaf4ef] p-6 sm:p-8" role="status" aria-live="polite" data-id="element-35" data-mp="3fdnrwbv0qxq9">
        <div className="flex items-start gap-4" data-id="element-36" data-mp="36e8zh6vakd34">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#286151] text-white" data-id="element-37" data-mp="36fk8pm7iad1m">
            <CheckCircle2Icon className="h-5 w-5" aria-hidden="true" data-id="element-38" data-mp="3bdlo9x5q3u8c" />
          </span>
          <div data-id="element-39" data-mp="2whe8d91cleyv">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#286151]" data-id="element-40" data-mp="2wguwa8ydpksr">
              Your enquiry is ready
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#172820]" style={{
            fontFamily: 'var(--font-display)'
          }} data-id="element-41" data-mp="2iaax64rznut4">
              Review your email before sending.
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-6 text-[#3e564b]" data-id="element-42" data-mp="28in5c2risbr4">
              Your project details have been formatted into an email draft. Open
              it, review the details, then send it when you are ready.
            </p>
            <a href={mailtoUrl} className="mt-5 inline-flex items-center gap-2 rounded-sm bg-[#172820] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#286151] focus:outline-none focus:ring-2 focus:ring-[#286151] focus:ring-offset-2" data-id="element-43" data-mp="2wa2eaedsxrbc">
              <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-44" data-mp="1fwc89234gsz2" /> Open email
              draft
            </a>
          </div>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="space-y-10" aria-describedby="form-guidance" data-id="element-45" data-mp="3mkcnexv95pl0">
      <p id="form-guidance" className="text-sm leading-6 text-stone-600" data-id="element-46" data-mp="3rinyfbxaa64t">
        Fields marked <span className="font-semibold text-[#b7543e]" data-id="element-47" data-mp="cmfyiazoa95">*</span>{' '}
        help shape the first conversation. The more operational detail you can
        share, the more useful the reply can be.
      </p>
      <DiscoveryContactDetails values={values} errors={errors} updateField={updateField} data-id="element-48" data-mp="a3phfl7tyr8y" />
      <DiscoveryProjectDetails values={values} errors={errors} updateField={updateField} data-id="element-49" data-mp="e380ebg3cs95" />
      <DiscoverySystemContext values={values} errors={errors} updateField={updateField} data-id="element-50" data-mp="j4f7076hzle8" />
      <DiscoveryPeopleContext values={values} errors={errors} updateField={updateField} data-id="element-51" data-mp="o0cncdhrgrr0" />

      <div className="border-t border-stone-200 pt-7" data-id="element-52" data-mp="2ptk3tq941feh">
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-6 text-stone-600" data-id="element-53" data-mp="2puc073aj7h5t">
          <input id="consent" name="consent" type="checkbox" checked={values.consent} onChange={event => updateField('consent', event.target.checked)} className={`mt-1 h-4 w-4 rounded border-stone-300 text-[#286151] focus:ring-[#286151] ${errors.consent ? 'border-[#b7543e] focus:ring-[#edc2b8]' : ''}`} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consent-error' : undefined} data-id="element-54" data-mp="k3yngv95fpfs" />
          <span data-id="element-55" data-mp="3sjs214jx6qu7">
            I am happy for Daanish to use these details to respond to this
            enquiry.{' '}
            <span className="font-semibold text-[#b7543e]" aria-hidden="true" data-id="element-56" data-mp="5h5ee7q83fw1">
              *
            </span>
          </span>
        </label>
        {errors.consent && <p id="consent-error" className="mt-2 text-sm font-medium text-[#b7543e]" data-id="element-57" data-mp="3duccfrw4krg3">
            {errors.consent}
          </p>}
        <button type="submit" className="group mt-7 inline-flex items-center gap-2 rounded-sm bg-[#172820] px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#286151] focus:outline-none focus:ring-2 focus:ring-[#286151] focus:ring-offset-2" data-id="element-58" data-mp="2zplvo09pna4a">
          Prepare my enquiry{' '}
          <SendIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-59" data-mp="2upr19mqq82eh" />
        </button>
        <p className="mt-3 text-xs leading-5 text-stone-500" data-id="element-60" data-mp="3neoirnn45v66">
          Preparing the enquiry creates a pre-filled email draft addressed to
          hello@daanish.co. You stay in control of sending it.
        </p>
      </div>
    </form>;
}